import React, { useState, useEffect, useRef } from 'react';
import { Navbar } from './components/Navbar';
import { Hero3D } from './components/Hero3D';
import { CategoriesSection } from './components/CategoriesSection';
import { ProductCard } from './components/ProductCard';
import { ProductDetailModal } from './components/ProductDetailModal';
import { CartDrawer } from './components/CartDrawer';
import { CheckoutModal } from './components/CheckoutModal';
import { CountriesPage } from './components/CountriesPage';
import { ContactPage } from './components/ContactPage';
import { CategoriesPage } from './components/CategoriesPage';
import { AIAssistantModal } from './components/AIAssistantModal';
import { Footer } from './components/Footer';
import { LegalModal, LegalTab } from './components/LegalModal';
import { SEOHead } from './components/SEOHead';
import { SearchResultsPage } from './components/SearchResultsPage';
import { TestimonialsSection } from './components/TestimonialsSection';
import { CategoryMarqueeSection } from './components/CategoryMarqueeSection';
import { ProductSkeletonGrid } from './components/ProductSkeleton';
import { WelcomeFlow } from './components/WelcomeFlow';
import { CrispChatWidget } from './components/CrispChatWidget';
import { getSafeProductFirstImage, hasProductRealImage, KNOWN_DELETED_IDS } from './lib/productImages';
import { Product, CartItem, Coupon, Currency, DocumentCategory, Order, SoftwareFormat, CMSContent, CustomerUser, ReviewItem, DownloadLog, DynamicPaymentGateway, Testimonial, TestimonialsConfig, MarqueeConfig } from './types';
import initialProductsData from './data/initialProducts.json';
import { getLocalMarqueeConfig, subscribeToMarqueeConfigFromFirestore } from './lib/marqueeService';
import { fetchProductsFromFirestore, saveProductToFirestore, removeProductFromFirestore, subscribeToProductsInFirestore, getProductCreatedTimestamp, safeCacheProducts, getDeletedProductIds, getLocalCachedProducts, markProductAsDeleted, unmarkProductAsDeleted } from './lib/productsService';
import { auth, db } from './lib/firebase';
import { doc, getDoc } from 'firebase/firestore';
import { subscribeToAuthChanges, isKnownAdminEmail } from './lib/authService';
import { subscribeToOrdersInFirestore, subscribeToUserOrdersInFirestore } from './lib/ordersService';
import { subscribeToUsersInFirestore } from './lib/usersService';
import { subscribeToReviewsInFirestore } from './lib/reviewsService';
import { subscribeToDownloadsInFirestore } from './lib/downloadsService';
import { subscribeToCountriesFromFirestore, CountryModel, INITIAL_COUNTRIES } from './lib/countriesService';
import { subscribeToCMSFromFirestore, DEFAULT_CMS_CONTENT } from './lib/cmsService';
import { subscribeToDynamicPaymentGatewaysFromFirestore, DEFAULT_DYNAMIC_GATEWAYS, getInitialGateways } from './lib/paymentGatewayService';
import {
  fetchTestimonialsFromFirestore,
  fetchTestimonialsConfigFromFirestore,
  subscribeToTestimonialsInFirestore,
  subscribeToTestimonialsConfigInFirestore,
  getLocalCachedTestimonials,
  getLocalCachedTestimonialsConfig
} from './lib/testimonialsService';
import { computeLiveStatistics, isProductPublished } from './lib/statsService';
import {
  getCategoryBySlug,
  getCategorySlug,
  getCountryBySlug,
  getCountrySlug,
  getProductSlug,
  findProductBySlug,
} from './lib/slugUtils';
import { CustomerInvoiceView } from './components/CustomerInvoiceView';
import { SlidersHorizontal, Sparkles, Filter, CheckCircle2, ArrowUpDown, ChevronLeft, ChevronRight, AlertCircle } from 'lucide-react';

const UserDashboard = React.lazy(() => import('./components/UserDashboard').then(module => ({ default: module.UserDashboard })));
const AdminDashboard = React.lazy(() => import('./components/AdminDashboard').then(module => ({ default: module.AdminDashboard })));
const INITIAL_PRODUCTS = initialProductsData as unknown as Product[];

function getInitialProductsCache(): Product[] {
  return getLocalCachedProducts();
}

export default function App() {
  // Theme & Currency
  const [darkMode, setDarkMode] = useState<boolean>(true);
  const [currency, setCurrency] = useState<Currency>('USD');

  // Sync background colors with theme to prevent white flash on scroll (rubber-banding)
  useEffect(() => {
    if (typeof document !== 'undefined') {
      const bgColor = darkMode ? '#020617' : '#f8fafc'; // slate-950 and slate-50
      document.body.style.backgroundColor = bgColor;
      document.documentElement.style.backgroundColor = bgColor;
    }
  }, [darkMode]);

  // Navigation State
  const [invoiceToken, setInvoiceToken] = useState('');
  const [activeTab, setActiveTab] = useState<'marketplace' | 'categories' | 'countries' | 'contact' | 'user' | 'admin' | 'search'>('marketplace');
  const [selectedCountry, setSelectedCountry] = useState<string | null>(null);

  // Products State (Instant Local Cache + Real-Time Firestore Background Hydration)
  const initialCache = getInitialProductsCache();
  const [allProducts, setAllProducts] = useState<Product[]>(initialCache);
  const [hasFirestoreLoaded, setHasFirestoreLoaded] = useState<boolean>(false);
  const [isProductsSyncedFromServer, setIsProductsSyncedFromServer] = useState<boolean>(false);
  const [isProductsLoading, setIsProductsLoading] = useState<boolean>(() => getInitialProductsCache().length === 0);
  const [filteredProducts, setFilteredProducts] = useState<Product[]>(initialCache);
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedCategory, setSelectedCategory] = useState<DocumentCategory | 'All'>('All');
  const [selectedFormat, setSelectedFormat] = useState<SoftwareFormat | 'All'>('All');
  const [sortBy, setSortBy] = useState<'latest' | 'popular' | 'price-low' | 'price-high' | 'rating'>('latest');
  const [firestoreError, setFirestoreError] = useState<string | null>(null);

  // Pagination State for Homepage (Page 1 = 6 products, Page 2+ = 9 products, 3 columns layout)
  const [currentPage, setCurrentPage] = useState<number>(1);

  // Interactive Modals & Drawers State
  const [quickViewProduct, setQuickViewProduct] = useState<Product | null>(null);
  const [pendingProductSlug, setPendingProductSlug] = useState<string | null>(null);
  const [cartDrawerOpen, setCartDrawerOpen] = useState(false);
  const [checkoutModalOpen, setCheckoutModalOpen] = useState(false);
  const [aiModalOpen, setAiModalOpen] = useState(false);
  const [legalModalTab, setLegalModalTab] = useState<LegalTab | null>(null);

  // Products Ref to decouple routing effects from product state changes
  const allProductsRef = useRef<Product[]>(allProducts);
  useEffect(() => {
    allProductsRef.current = allProducts;
  }, [allProducts]);

  // Handle async deep linking (e.g. preview links) when Firestore finishes loading
  useEffect(() => {
    if (hasFirestoreLoaded && allProducts.length > 0) {
      if (typeof window !== 'undefined') {
        const pathLower = window.location.pathname.toLowerCase();
        const urlParams = new URLSearchParams(window.location.search);
        const productParam = urlParams.get('product') || urlParams.get('id') || urlParams.get('item') || urlParams.get('p');
        const prodMatch = pathLower.match(/^\/(?:product|products|p|item)\/([^\/]+)/);
        const targetProdKey = productParam || (prodMatch ? prodMatch[1] : null);

        if (targetProdKey) {
          const found = findProductBySlug(allProducts, targetProdKey);
          if (found) {
            setPendingProductSlug(null);
            setQuickViewProduct(found);
            if (pathLower.startsWith('/categories')) setActiveTab('categories');
            else if (pathLower.startsWith('/countries')) setActiveTab('countries');
            else setActiveTab('marketplace');
          } else if (isProductsSyncedFromServer) {
            // Only give up and close the modal if the SERVER has finished syncing
            // and the product STILL isn't found.
            setPendingProductSlug(null);
          }
        }
      }
    }
  }, [hasFirestoreLoaded, allProducts, isProductsSyncedFromServer]);

  // Router sync with window location
  useEffect(() => {
    const syncRouteWithState = () => {
      if (typeof window === 'undefined') return;
      const path = window.location.pathname;
      const pathLower = path.toLowerCase();
      const urlParams = new URLSearchParams(window.location.search);
      const qParam = urlParams.get('q') || urlParams.get('search');
      const orderSuccessParam = urlParams.get('orderSuccess');

      if (orderSuccessParam) {
        setActiveTab('user');
        setQuickViewProduct(null);
        try {
          localStorage.removeItem('rh_active_nowpayments_order');
          localStorage.removeItem('rh_active_gateway_order');
        } catch (e) {}
        return;
      }

      const currentProducts = allProductsRef.current;

      const invMatch = pathLower.match(/^\/invoice\/([^\/]+)/);
      if (invMatch) {
         setInvoiceToken(invMatch[1]);
         setActiveTab('invoice_view' as any);
         return;
      }


      // 1. Direct Product Route or Query Param Check FIRST
      const productParam = urlParams.get('product') || urlParams.get('id') || urlParams.get('item') || urlParams.get('p');
      const prodMatch = pathLower.match(/^\/(?:product|products|p|item)\/([^\/]+)/);
      const targetProdKey = productParam || (prodMatch ? prodMatch[1] : null);

      if (targetProdKey) {
        const found = findProductBySlug(currentProducts, targetProdKey);
        if (!found) {
           setPendingProductSlug(targetProdKey);
           
           // Attempt a direct ID fetch in parallel (fastest if targetProdKey is an exact doc ID)
           getDoc(doc(db, 'products', targetProdKey)).then(snap => {
              if (snap.exists() && typeof window !== 'undefined') {
                 setQuickViewProduct({ id: snap.id, ...snap.data() } as any);
                 setPendingProductSlug(null);
              }
           }).catch(() => {});
        }
        if (found) {
          setQuickViewProduct(found);
          if (pathLower.startsWith('/categories')) {
            setActiveTab('categories');
          } else if (pathLower.startsWith('/countries')) {
            setActiveTab('countries');
          } else {
            setActiveTab('marketplace');
          }
          return;
        }
      }

      // 2. Search Route: /search?q=... or ?search=...
      if (qParam || pathLower.startsWith('/search')) {
        if (qParam) setSearchQuery(qParam);
        setActiveTab('search');
        setQuickViewProduct(null);
        return;
      }

      // 3. Category Route: /category/:slug or /categories/:slug
      const catMatch = pathLower.match(/^\/(?:category|categories)\/([^\/]+)/);
      if (catMatch && catMatch[1]) {
        const cat = getCategoryBySlug(catMatch[1]);
        if (cat) {
          setSelectedCategory(cat);
          setSelectedCountry(null);
          setActiveTab('categories');
          setQuickViewProduct(null);
          return;
        }
      }

      // 4. Country Route: /country/:slug or /countries/:slug
      const countryMatch = pathLower.match(/^\/(?:country|countries)\/([^\/]+)/);
      if (countryMatch && countryMatch[1]) {
        const countryName = getCountryBySlug(countryMatch[1]);
        if (countryName) {
          setSelectedCountry(countryName);
          setActiveTab('countries');
          setQuickViewProduct(null);
          return;
        }
      }

      // 5. Standard Tab Routes
      if (pathLower.startsWith('/categories')) {
        setActiveTab('categories');
        setSelectedCategory('All');
        setSelectedCountry(null);
      } else if (pathLower.startsWith('/countries')) {
        setActiveTab('countries');
        setSelectedCountry(null);
      } else if (pathLower.startsWith('/contact')) {
        setActiveTab('contact');
      } else if (pathLower.startsWith('/user') || pathLower.startsWith('/dashboard')) {
        setActiveTab('user');
      } else if (pathLower.startsWith('/admin') || pathLower.startsWith('/rh-control')) {
        setActiveTab('admin');
      } else {
        setActiveTab('marketplace');
      }
    };

    syncRouteWithState();
    window.addEventListener('popstate', syncRouteWithState);
    return () => window.removeEventListener('popstate', syncRouteWithState);
  }, []);

  // Navigation Callbacks
  const handlePerformSearch = (q: string) => {
    setSearchQuery(q);
    setActiveTab('search');
    if (typeof window !== 'undefined') {
      const searchUrl = `/search?q=${encodeURIComponent(q)}`;
      window.history.pushState({ path: searchUrl }, '', searchUrl);
      window.scrollTo({ top: 0, behavior: 'smooth' });
    }
  };

  const handleNavigateCategory = (cat: DocumentCategory) => {
    setSelectedCategory(cat);
    setSelectedCountry(null);
    setActiveTab('categories');
    if (typeof window !== 'undefined') {
      const url = `/categories/${getCategorySlug(cat)}`;
      window.history.pushState({ path: url }, '', url);
      window.scrollTo({ top: 0, behavior: 'smooth' });
    }
  };

  const handleNavigateCountry = (countryName: string | null) => {
    if (!countryName) {
      setSelectedCountry(null);
      setActiveTab('countries');
      if (typeof window !== 'undefined') {
        window.history.pushState({ path: '/countries' }, '', '/countries');
      }
      return;
    }
    setSelectedCountry(countryName);
    setActiveTab('countries');
    if (typeof window !== 'undefined') {
      const url = `/country/${getCountrySlug(countryName)}`;
      window.history.pushState({ path: url }, '', url);
      window.scrollTo({ top: 0, behavior: 'smooth' });
    }
  };

  const handleNavigateProduct = (product: Product) => {
    setQuickViewProduct(product);
    if (typeof window !== 'undefined') {
      const url = `/product/${getProductSlug(product)}`;
      window.history.pushState({ path: url }, '', url);
      
      // Notify Crisp about the product view (only if not admin)
      if ((window as any).$crisp && activeTab !== 'admin' && product) {
        try {
          const safeTitle = String(product.title || '').trim().slice(0, 100);
          const safeId = String(product.id || '').trim();
          const safePrice = typeof product.price === 'number' ? product.price : parseFloat(String(product.price)) || 0;

          (window as any).$crisp.push([
            "set",
            "session:data",
            [[["viewing_product", safeTitle]]]
          ]);

          (window as any).$crisp.push([
            "set",
            "session:event",
            [[["view_product", { title: safeTitle, id: safeId, price: safePrice }, "blue"]]]
          ]);
        } catch (e) {}
      }
    }
  };

  const handleCloseProductModal = () => {
    setQuickViewProduct(null);
    setPendingProductSlug(null);
    if (typeof window !== 'undefined' && window.location.pathname.includes('/product')) {
      let fallback = '/';
      if (selectedCategory !== 'All') {
        fallback = `/category/${getCategorySlug(selectedCategory)}`;
      } else if (selectedCountry) {
        fallback = `/country/${getCountrySlug(selectedCountry)}`;
      } else if (activeTab !== 'marketplace') {
        fallback = activeTab === 'admin' ? '/rh-control' : `/${activeTab}`;
      }
      window.history.pushState({ path: fallback }, '', fallback);
    }
  };

  const handleTabChange = (tab: 'marketplace' | 'categories' | 'countries' | 'contact' | 'user' | 'admin' | 'search') => {
    setActiveTab(tab);
    let path = '/';
    if (tab === 'categories') {
      path = selectedCategory !== 'All' ? `/categories/${getCategorySlug(selectedCategory)}` : '/categories';
    } else if (tab === 'countries') {
      path = selectedCountry ? `/country/${getCountrySlug(selectedCountry)}` : '/countries';
    } else if (tab === 'contact') {
      path = '/contact';
    } else if (tab === 'user') {
      path = '/user';
    } else if (tab === 'admin') {
      path = '/rh-control';
    } else if (tab === 'search') {
      path = `/search?q=${encodeURIComponent(searchQuery)}`;
    }

    if (typeof window !== 'undefined') {
      window.history.pushState({ path }, '', path);
      window.scrollTo({ top: 0, behavior: 'smooth' });
    }
  };

  // Cart & Wishlist Local State
  const [cartItems, setCartItems] = useState<CartItem[]>(() => {
    try {
      const saved = localStorage.getItem('rh_cart');
      return saved ? JSON.parse(saved) : [];
    } catch {
      return [];
    }
  });

  const [wishlistItems, setWishlistItems] = useState<Product[]>(() => {
    try {
      const saved = localStorage.getItem('rh_wishlist');
      return saved ? JSON.parse(saved) : [];
    } catch {
      return [];
    }
  });

  const [appliedCoupon, setAppliedCoupon] = useState<Coupon | null>(null);
  const [userOrders, setUserOrders] = useState<Order[]>(() => {
    try {
      const saved = localStorage.getItem('rh_orders_cache');
      if (saved) {
        const parsed = JSON.parse(saved);
        if (Array.isArray(parsed) && parsed.length > 0) {
          return parsed;
        }
      }
    } catch (e) {}
    return [];
  });

  // CMS Content State
  const [cmsContent, setCmsContent] = useState<CMSContent>(() => {
    try {
      const saved = localStorage.getItem('rh_cms_content');
      if (saved) return { ...DEFAULT_CMS_CONTENT, ...JSON.parse(saved) };
    } catch (e) {
      console.warn(e);
    }
    return DEFAULT_CMS_CONTENT;
  });

  // Toast Notification State
  const [toastMessage, setToastMessage] = useState<string | null>(null);
  const [currentUser, setCurrentUser] = useState<any>(null);

  useEffect(() => {
    try {
      localStorage.setItem('rh_cart', JSON.stringify(cartItems));
    } catch (e) {
      console.warn(e);
    }
  }, [cartItems]);

  useEffect(() => {
    try {
      localStorage.setItem('rh_wishlist', JSON.stringify(wishlistItems));
    } catch (e) {
      console.warn(e);
    }
  }, [wishlistItems]);

  const showToast = (msg: string) => {
    setToastMessage(msg);
    setTimeout(() => setToastMessage(null), 3500);
  };

  // Additional live collection states (with LocalStorage cache for 0ms admin dashboard rendering)
  const [usersList, setUsersList] = useState<CustomerUser[]>(() => {
    try {
      const saved = localStorage.getItem('rh_users_cache');
      return saved ? JSON.parse(saved) : [];
    } catch { return []; }
  });
  const [reviewsList, setReviewsList] = useState<ReviewItem[]>(() => {
    try {
      const saved = localStorage.getItem('rh_reviews_cache');
      return saved ? JSON.parse(saved) : [];
    } catch { return []; }
  });
  const [downloadsList, setDownloadsList] = useState<DownloadLog[]>(() => {
    try {
      const saved = localStorage.getItem('rh_downloads_cache');
      return saved ? JSON.parse(saved) : [];
    } catch { return []; }
  });
  const [countriesList, setCountriesList] = useState<CountryModel[]>(INITIAL_COUNTRIES);
  const [dynamicGateways, setDynamicGateways] = useState<DynamicPaymentGateway[]>(getInitialGateways);
  const [testimonialsList, setTestimonialsList] = useState<Testimonial[]>(getLocalCachedTestimonials());
  const [testimonialsConfig, setTestimonialsConfig] = useState<TestimonialsConfig>(getLocalCachedTestimonialsConfig());
  const [marqueeConfig, setMarqueeConfig] = useState<MarqueeConfig>(getLocalMarqueeConfig());

  // Real-Time Firestore Synchronization across all master collections
  useEffect(() => {
    let unsubProducts: (() => void) | undefined;
    let unsubOrders: (() => void) | undefined;
    let unsubUsers: (() => void) | undefined;
    let unsubReviews: (() => void) | undefined;
    let unsubDownloads: (() => void) | undefined;
    let unsubCountries: (() => void) | undefined;
    let unsubCMS: (() => void) | undefined;
    let unsubGateways: (() => void) | undefined;
    let unsubTestimonials: (() => void) | undefined;
    let unsubTestimonialsConfig: (() => void) | undefined;
    let unsubMarqueeConfig: (() => void) | undefined;

    const deduplicateProducts = (prods: Product[]) => {
      const deletedIds = getDeletedProductIds();
      const map = new Map<string, Product>();
      try {
        (prods || []).forEach((p, index) => {
          if (!p || typeof p !== 'object') return;
          if (
            (p as any).isDeleted === true ||
            p.status === 'deleted' ||
            (p.id && (deletedIds.has(p.id) || KNOWN_DELETED_IDS.includes(p.id)))
          ) {
            return;
          }
          const safeId = p.id || `rh-fs-${index}`;
          map.set(safeId, { ...p, id: safeId });
        });
      } catch (err) {
        console.error('Error in deduplicateProducts loop:', err);
      }
      const result = Array.from(map.values()).filter(p => !deletedIds.has(p.id) && !KNOWN_DELETED_IDS.includes(p.id) && !(p as any).isDeleted && p.status !== 'deleted');
      safeCacheProducts(result);
      return result;
    };

    const cacheExists = getInitialProductsCache().length > 0;

    // Fast preloader finish for returning visitors with local cache (200ms smooth fade)
    const quickCacheTimer = setTimeout(() => {
      if (cacheExists) {
        setIsProductsLoading(false);
      }
    }, 200);

    // Maximum safety timer (800ms) to ensure preloader never gets stuck
    const maxSafetyTimer = setTimeout(() => {
      setIsProductsLoading(false);
    }, 800);

    // Real-Time Subscriptions for Public Marketplace Data
    try {
      unsubProducts = subscribeToProductsInFirestore((prods, source) => {
        const deduplicated = deduplicateProducts(prods || []);
        setAllProducts(deduplicated);
        setHasFirestoreLoaded(true);
        if (source === 'server') {
          setIsProductsSyncedFromServer(true);
        }
        setIsProductsLoading(false);
        if (deduplicated && deduplicated.length > 0) {
          safeCacheProducts(deduplicated);
        }
      }, (err) => {
        console.error('Firestore products loading failed:', err);
        setFirestoreError(err.message || String(err));
        setIsProductsLoading(false);
      });
      unsubCountries = subscribeToCountriesFromFirestore((countries) => {
        setCountriesList(countries);
      });
      unsubCMS = subscribeToCMSFromFirestore((cmsData) => {
        if (cmsData) {
          setCmsContent(cmsData);
        }
      });
      unsubGateways = subscribeToDynamicPaymentGatewaysFromFirestore((gateways) => {
        if (gateways) {
          setDynamicGateways(gateways);
        }
      });
      unsubTestimonials = subscribeToTestimonialsInFirestore((tList) => {
        if (tList) setTestimonialsList(tList);
      });
      unsubTestimonialsConfig = subscribeToTestimonialsConfigInFirestore((cfg) => {
        if (cfg) setTestimonialsConfig(cfg);
      });
      unsubMarqueeConfig = subscribeToMarqueeConfigFromFirestore((cfg) => {
        if (cfg) setMarqueeConfig(cfg);
      });
    } catch (e) {
      console.warn('Realtime listener error:', e);
    }

    return () => {
      clearTimeout(quickCacheTimer);
      clearTimeout(maxSafetyTimer);
      if (unsubProducts) unsubProducts();
      if (unsubOrders) unsubOrders();
      if (unsubCountries) unsubCountries();
      if (unsubCMS) unsubCMS();
      if (unsubGateways) unsubGateways();
      if (unsubTestimonials) unsubTestimonials();
      if (unsubTestimonialsConfig) unsubTestimonialsConfig();
      if (unsubMarqueeConfig) unsubMarqueeConfig();
    };
  }, []);

  useEffect(() => {
    const unsubAuth = subscribeToAuthChanges((u) => {
      setCurrentUser(u);
    });
    return () => {
      if (unsubAuth) unsubAuth();
    };
  }, []);

  // Cross-tab real-time sync: when an admin in another tab adds, edits, or deletes a product,
  // update the marketplace homepage instantly without requiring manual page reload!
  useEffect(() => {
    if (typeof window === 'undefined') return;

    let syncChannel: BroadcastChannel | null = null;
    if ('BroadcastChannel' in window) {
      try {
        syncChannel = new BroadcastChannel('rh_products_realtime_sync');
        syncChannel.onmessage = (event) => {
          if (event.data && event.data.type === 'PRODUCTS_MUTATED' && Array.isArray(event.data.products)) {
            const deletedIds = getDeletedProductIds();
            const valid = event.data.products.filter((p: Product) => p && p.id && !deletedIds.has(p.id));
            setAllProducts(valid);
          }
        };
      } catch {}
    }

    const handleStorage = (e: StorageEvent) => {
      if ((e.key === 'rh_cached_products' || e.key === 'rh_deleted_product_ids') && e.newValue) {
        try {
          const cached = getInitialProductsCache();
          if (cached && cached.length > 0) {
            setAllProducts(cached);
          }
        } catch {}
      }
    };

    window.addEventListener('storage', handleStorage);
    return () => {
      window.removeEventListener('storage', handleStorage);
      if (syncChannel) syncChannel.close();
    };
  }, []);

  // Lazy-load user & admin subscriptions ONLY when user or admin tab is active
  useEffect(() => {
    if (activeTab === 'user') {
      // Orders are now fetched earlier
      return;
    }
  }, [activeTab]);

  // Authoritative real-time data subscriptions based on logged-in user role or active admin tab
  useEffect(() => {
    let unsubUserOrders: (() => void) | undefined;
    let unsubUsers: (() => void) | undefined;
    let unsubReviews: (() => void) | undefined;
    let unsubDownloads: (() => void) | undefined;

    const userEmail = (currentUser?.email || auth.currentUser?.email || '').toLowerCase().trim();
    const isAdminSession = (currentUser && currentUser.role === 'admin') || activeTab === 'admin' || isKnownAdminEmail(userEmail);

    if (isAdminSession) {
      // Start admin subscriptions
      unsubUserOrders = subscribeToOrdersInFirestore((orders) => {
        setUserOrders(orders);
        try { localStorage.setItem('rh_orders_cache', JSON.stringify(orders)); } catch (e) {}
      });
      unsubUsers = subscribeToUsersInFirestore((users) => {
        setUsersList(users);
        try { localStorage.setItem('rh_users_cache', JSON.stringify(users)); } catch (e) {}
      });
      unsubReviews = subscribeToReviewsInFirestore((reviews) => {
        setReviewsList(reviews);
        try { localStorage.setItem('rh_reviews_cache', JSON.stringify(reviews)); } catch (e) {}
      });
      unsubDownloads = subscribeToDownloadsInFirestore((downloads) => {
        setDownloadsList(downloads);
        try { localStorage.setItem('rh_downloads_cache', JSON.stringify(downloads)); } catch (e) {}
      });
    } else if (currentUser && currentUser.email) {
      // Customer subscriptions
      unsubUserOrders = subscribeToUserOrdersInFirestore(currentUser.email, (orders) => {
        setUserOrders(orders);
        try { localStorage.setItem('rh_orders_cache', JSON.stringify(orders)); } catch (e) {}
      });
      unsubDownloads = subscribeToDownloadsInFirestore((downloads) => {
        setDownloadsList(downloads);
        try { localStorage.setItem('rh_downloads_cache', JSON.stringify(downloads)); } catch (e) {}
      });
    }

    return () => {
      if (unsubUserOrders) unsubUserOrders();
      if (unsubUsers) unsubUsers();
      if (unsubReviews) unsubReviews();
      if (unsubDownloads) unsubDownloads();
    };
  }, [currentUser, activeTab]);

  // Enterprise Live Statistics aggregated in real-time from Firestore data
  const liveStats = React.useMemo(() => {
    return computeLiveStatistics({
      products: allProducts,
      orders: userOrders,
      users: usersList,
      reviews: reviewsList,
      downloads: downloadsList,
      wishlistCount: wishlistItems.length,
    });
  }, [allProducts, userOrders, usersList, reviewsList, downloadsList, wishlistItems.length]);

  // Filter & sort products whenever state changes
  useEffect(() => {
    const deletedIds = getDeletedProductIds();
    let result = allProducts.filter(p =>
      p &&
      p.id &&
      !deletedIds.has(p.id) &&
      !KNOWN_DELETED_IDS.includes(p.id) &&
      !(p as any).isDeleted &&
      p.status !== 'deleted' &&
      isProductPublished(p)
    );

    if (searchQuery) {
      const q = (typeof searchQuery === 'string' ? searchQuery.toLowerCase() : '');
      result = result.filter(
        p =>
          (typeof p.title === 'string' ? p.title.toLowerCase() : '').includes(q) ||
          (typeof p.description === 'string' ? p.description.toLowerCase() : '').includes(q) ||
          (typeof p.category === 'string' ? p.category.toLowerCase() : '').includes(q) ||
          p.tags?.some(t => (t && typeof t === 'string' ? t.toLowerCase() : '').includes(q))
      );
    }

    if (selectedCategory !== 'All') {
      result = result.filter(p => p && (typeof p.category === 'string' ? p.category.toLowerCase() : '') === (typeof selectedCategory === 'string' ? selectedCategory.toLowerCase() : ''));
    }

    if (selectedFormat !== 'All') {
      result = result.filter(p => p && (p.formats || []).includes(selectedFormat));
    }

    if (sortBy === 'price-low') {
      result.sort((a, b) => a.price - b.price);
    } else if (sortBy === 'price-high') {
      result.sort((a, b) => b.price - a.price);
    } else if (sortBy === 'rating') {
      result.sort((a, b) => b.rating - a.rating);
    } else if (sortBy === 'popular') {
      result.sort((a, b) => b.salesCount - a.salesCount);
    } else {
      result.sort((a, b) => {
        const timeA = getProductCreatedTimestamp(a);
        const timeB = getProductCreatedTimestamp(b);
        if (timeB !== timeA) return timeB - timeA;
        return (b.id || '').localeCompare(a.id || '');
      });
    }

    setFilteredProducts(result);
    setCurrentPage(1);
  }, [allProducts, searchQuery, selectedCategory, selectedFormat, sortBy]);

  // Paginated Slice for Homepage Grid (Page 1 = 6 items [2 rows x 3 columns], Subsequent pages = 9 items max [3 rows x 3 columns])
  const PAGE_1_SIZE = 6;
  const OTHER_PAGE_SIZE = 9;

  let totalPages = 1;
  if (filteredProducts.length > PAGE_1_SIZE) {
    totalPages = 1 + Math.ceil((filteredProducts.length - PAGE_1_SIZE) / OTHER_PAGE_SIZE);
  }

  const validPage = Math.min(Math.max(1, currentPage), totalPages);

  let pStartIndex = 0;
  let pEndIndex = PAGE_1_SIZE;

  if (validPage === 1) {
    pStartIndex = 0;
    pEndIndex = Math.min(filteredProducts.length, PAGE_1_SIZE);
  } else {
    pStartIndex = PAGE_1_SIZE + (validPage - 2) * OTHER_PAGE_SIZE;
    pEndIndex = Math.min(filteredProducts.length, pStartIndex + OTHER_PAGE_SIZE);
  }

  const paginatedProducts = filteredProducts.slice(pStartIndex, pEndIndex);

  // Helper function to generate clean pagination button sequence (< Prev 1 2 3 4 5 Next >)
  const getPageNumbers = (current: number, total: number) => {
    const pages: (number | string)[] = [];
    if (total <= 7) {
      for (let i = 1; i <= total; i++) pages.push(i);
    } else {
      let start = Math.max(1, current - 2);
      let end = Math.min(total, start + 4);
      if (end - start < 4) {
        start = Math.max(1, end - 4);
      }
      if (start > 1) {
        pages.push(1);
        if (start > 2) pages.push('...');
      }
      for (let i = start; i <= end; i++) {
        pages.push(i);
      }
      if (end < total) {
        if (end < total - 1) pages.push('...');
        pages.push(total);
      }
    }
    return pages;
  };

  // Handlers
  const handleToggleWishlist = (product: Product) => {
    const exists = wishlistItems.some(item => item.id === product.id);
    if (exists) {
      setWishlistItems(prev => prev.filter(item => item.id !== product.id));
      showToast(`Removed "${product.title}" from Wishlist`);
    } else {
      setWishlistItems(prev => [...prev, product]);
      showToast(`Added "${product.title}" to Wishlist`);
    }
  };

  const handleQuickBuy = (product: Product, format?: SoftwareFormat) => {
    const isLoggedIn = localStorage.getItem('rh_user_logged_in') === 'true';
    let isVerified = true;
    try {
      const userRaw = localStorage.getItem('rh_current_user');
      if (userRaw) {
        const u = JSON.parse(userRaw);
        if (u && u.emailVerified === false) {
          isVerified = false;
        }
      }
    } catch (e) {}

    if (!isLoggedIn) {
      setActiveTab('user');
      showToast('🔒 Sign In Required: Please login to your Customer Account to proceed with Checkout.');
      return;
    }
    if (!isVerified) {
      setActiveTab('user');
      showToast('🔒 Email Verification Required: Please enter the 6-digit verification code sent to your email.');
      return;
    }
    handleAddToCart(product, format);
    setCheckoutModalOpen(true);
  };

  const handleAddToCart = (product: Product, format?: SoftwareFormat) => {
    const chosenFormat = format || (product.formats && product.formats[0]) || 'PSD';
    setCartItems(prev => {
      const idx = prev.findIndex(i => i.product.id === product.id && i.selectedFormat === chosenFormat);
      if (idx > -1) {
        const copy = [...prev];
        copy[idx].quantity += 1;
        return copy;
      }
      return [...prev, { product, quantity: 1, selectedFormat: chosenFormat }];
    });
    showToast(`Added .${chosenFormat} template to Cart`);
  };

  const handleUpdateQuantity = (productId: string, qty: number) => {
    setCartItems(prev => prev.map(item => item.product.id === productId ? { ...item, quantity: qty } : item));
  };

  const handleRemoveCartItem = (productId: string) => {
    setCartItems(prev => prev.filter(item => item.product.id !== productId));
  };

  const handleApplyCoupon = async (code: string): Promise<boolean> => {
    if (code.toUpperCase() === 'RH3D20' || code.toUpperCase() === 'WELCOME10') {
      setAppliedCoupon({
        code: code.toUpperCase(),
        discountPercent: 20,
        expiryDate: '2026-12-31',
        description: '20% Off Master Pass'
      });
      return true;
    }
    return false;
  };

  // Admin Firestore Operations
  const handleAddProductAdmin = async (newP: Partial<Product>) => {
    const previewImages = Array.isArray(newP.previewImages) && newP.previewImages.length > 0
      ? newP.previewImages
      : ((newP as any).image ? [(newP as any).image] : []);
    const image = (previewImages.length > 0 ? previewImages[0] : ((newP as any).image || ''));
    const finalStatus = newP.status || 'published';
    const isPub = finalStatus.toLowerCase() === 'published' || finalStatus.toLowerCase() === 'active';

    const p: Product = {
      id: newP.id || `rh-${Date.now().toString(36)}-${Math.random().toString(36).substring(2, 7)}`,
      title: newP.title || 'Untitled Document Template',
      category: newP.category || 'Passport',
      country: newP.country || 'USA',
      status: finalStatus,
      description: newP.description || 'High quality 3D vector document template.',
      detailedFeatures: newP.detailedFeatures || ['300 DPI CMYK Print Ready', 'Smart Layer Slots'],
      price: newP.price || 29.99,
      originalPrice: newP.originalPrice || 49.99,
      discountPercent: newP.discountPercent || 40,
      rating: newP.rating ?? 5.0,
      reviewCount: newP.reviewCount ?? 1,
      salesCount: newP.salesCount ?? 1,
      isPopular: newP.isPopular ?? false,
      formats: newP.formats || ['PSD', 'AI'],
      previewImages,
      image,
      downloadFileUrl: newP.downloadFileUrl || (newP as any).downloadUrl || (newP as any).fileUrl || (newP as any).driveUrl || '',
      specs: newP.specs || {
        resolution: '3000 x 2000 px',
        colorMode: 'CMYK',
        dpi: 300,
        layers: 'Organized Vector Layers',
        fontsIncluded: true,
        printReady: true,
        fileSize: '65 MB',
        softwareRequired: ['Adobe Photoshop']
      },
      reviews: [],
      tags: newP.tags || ['template'],
      createdAt: new Date().toISOString(),
      updatedAt: new Date().toISOString()
    };
    (p as any).isPublished = isPub;
    (p as any).published = isPub;
    if (!isPub) {
      delete (p as any).publishedAt;
      delete (p as any).publishedDate;
      delete (p as any).publishDate;
    }

    unmarkProductAsDeleted(p.id);

    try {
      setAllProducts(prev => [p, ...prev.filter(item => item.id !== p.id)]);
      await saveProductToFirestore(p, true);
      showToast(isPub ? `Published "${p.title}" to Cloud Database` : `Created draft "${p.title}" (Unpublished)`);
    } catch (error: any) {
      showToast(`Failed to save product: ${error.message}`);
      throw error;
    }
  };

  const handleUpdateProductAdmin = async (updatedP: Product) => {
    unmarkProductAsDeleted(updatedP.id);
    const nowIso = new Date().toISOString();
    const previewImages = Array.isArray(updatedP.previewImages) ? updatedP.previewImages : (updatedP.image ? [updatedP.image] : []);
    const image = updatedP.image || (previewImages.length > 0 ? previewImages[0] : '');
    const isPub = updatedP.status ? (updatedP.status === 'published' || updatedP.status === 'active') : true;
    const withUpdated: Product = {
      ...updatedP,
      status: updatedP.status || 'published',
      previewImages,
      image,
      updatedAt: nowIso
    };
    (withUpdated as any).isPublished = isPub;
    
    // Immediate optimistic update in App.tsx:
    // Move updated product to the top so admin and visitors instantly see the published changes!
    setAllProducts(prev => [withUpdated, ...prev.filter(p => p.id !== updatedP.id)]);

    try {
      await saveProductToFirestore(withUpdated, false);
      showToast(`Updated "${withUpdated.title}" in Cloud Database`);
    } catch (error: any) {
      showToast(`Failed to update product: ${error.message}`);
      throw error;
    }
  };

  const handleDeleteProductAdmin = async (productId: string) => {
    markProductAsDeleted(productId);
    // Immediate optimistic deletion in App.tsx
    setAllProducts(prev => prev.filter(p => p.id !== productId));
    setFilteredProducts(prev => prev.filter(p => p.id !== productId));
    try {
      await removeProductFromFirestore(productId);
      showToast(`Deleted product from Cloud Database`);
    } catch (error: any) {
      showToast(`Failed to delete product: ${error.message}`);
      throw error;
    }
  };

  return (
    <div className={`min-h-screen font-sans transition-colors duration-300 ${
      darkMode ? 'bg-slate-950 text-slate-100' : 'bg-slate-50 text-slate-900'
    }`}>
      {/* Interactive Robot Preloader & Emotions Splash Screen */}
      
      
      <WelcomeFlow />
      <CrispChatWidget isAdmin={activeTab === 'admin'} />

      {/* Dynamic SEO & JSON-LD Structured Data */}
      <SEOHead
        product={quickViewProduct}
        products={allProducts}
        activeTab={activeTab}
        selectedCategory={selectedCategory}
        selectedCountry={selectedCountry || undefined}
        searchQuery={searchQuery}
      />
      
      {/* Glass Top Navbar */}
      <Navbar
        darkMode={darkMode}
        setDarkMode={setDarkMode}
        currency={currency}
        setCurrency={setCurrency}
        cartCount={cartItems.reduce((acc, i) => acc + i.quantity, 0)}
        wishlistCount={wishlistItems.length}
        onOpenCart={() => setCartDrawerOpen(true)}
        onOpenWishlist={() => handleTabChange('user')}
        onOpenAI={() => setAiModalOpen(true)}
        activeTab={activeTab}
        setActiveTab={handleTabChange}
        searchQuery={searchQuery}
        setSearchQuery={setSearchQuery}
        products={allProducts}
        onSelectProduct={handleNavigateProduct}
        onPerformSearch={handlePerformSearch}
        onSelectCategory={handleNavigateCategory}
      />

      {/* Main Body Layout */}
      {activeTab === 'marketplace' && (
        <main>
          {/* 3D Modern Hero Section */}
          <Hero3D
            searchQuery={searchQuery}
            setSearchQuery={setSearchQuery}
            selectedCategory={selectedCategory}
            setSelectedCategory={(cat) => cat === 'All' ? handleTabChange('marketplace') : handleNavigateCategory(cat as DocumentCategory)}
            onOpenAI={() => setAiModalOpen(true)}
            darkMode={darkMode}
            products={allProducts}
            onSelectProduct={handleNavigateProduct}
            onPerformSearch={handlePerformSearch}
            cms={cmsContent}
            stats={liveStats}
          />

          {/* Categories 3D Cards */}
          <CategoriesSection
            selectedCategory={selectedCategory}
            setSelectedCategory={(cat) => cat === 'All' ? handleTabChange('marketplace') : handleNavigateCategory(cat as DocumentCategory)}
            onNavigateCategory={handleNavigateCategory}
            darkMode={darkMode}
            products={allProducts}
          />

          {/* Marketplace Grid & Filters Section */}
          <section className="py-12 relative z-10" id="featured-products">
            <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 space-y-8">
              
              {/* Filter Controls Bar */}
              <div className={`p-3 sm:p-4 rounded-3xl border flex flex-col sm:flex-row items-stretch sm:items-center justify-between gap-3 sm:gap-4 overflow-hidden ${
                darkMode ? 'glass-card border-white/10' : 'glass-card-light border-slate-200'
              }`}>
                
                {/* Category & Format Filter Badges (Horizontal Scrollable Chips on Mobile) */}
                <div className="flex items-center gap-2 overflow-x-auto flex-nowrap w-full sm:w-auto pb-1.5 sm:pb-0 scrollbar-none [&::-webkit-scrollbar]:hidden touch-pan-x">
                  <span className="text-xs font-bold text-slate-400 flex items-center gap-1 flex-shrink-0 whitespace-nowrap">
                    <Filter className="w-3.5 h-3.5 text-cyan-400" /> Format:
                  </span>
                  {(['All', 'PSD', 'AI', 'CDR'] as const).map((fmt) => (
                    <button
                      key={fmt}
                      onClick={() => setSelectedFormat(fmt)}
                      className={`px-3.5 py-1.5 rounded-xl text-xs font-bold border transition-all flex-shrink-0 whitespace-nowrap min-w-max ${
                        selectedFormat === fmt
                          ? 'bg-blue-600 text-white border-blue-400 shadow-md'
                          : darkMode
                          ? 'bg-slate-900/60 border-slate-800 text-slate-300 hover:bg-slate-800'
                          : 'bg-slate-100 border-slate-300 text-slate-700'
                      }`}
                    >
                      {fmt === 'All' ? 'All Formats' : `.${fmt}`}
                    </button>
                  ))}
                </div>

                {/* Sort Dropdown - Full width below chips on mobile, auto on desktop */}
                <div className="flex items-center justify-between sm:justify-end gap-2 w-full sm:w-auto pt-2.5 sm:pt-0 border-t sm:border-t-0 border-slate-800/60">
                  <span className="text-xs font-bold text-slate-400 flex items-center gap-1 flex-shrink-0 whitespace-nowrap">
                    <ArrowUpDown className="w-3.5 h-3.5 text-cyan-400" /> Sort By:
                  </span>
                  <select
                    value={sortBy}
                    onChange={(e) => setSortBy(e.target.value as any)}
                    className={`px-3 py-1.5 rounded-xl border text-xs font-bold focus:outline-none flex-1 sm:flex-initial max-w-[220px] sm:max-w-none ${
                      darkMode ? 'bg-slate-900 border-slate-700 text-white' : 'bg-white border-slate-300 text-slate-800'
                    }`}
                  >
                    <option value="latest">Latest Templates</option>
                    <option value="popular">Most Popular</option>
                    <option value="price-low">Price: Low to High</option>
                    <option value="price-high">Price: High to Low</option>
                    <option value="rating">Highest Rated</option>
                  </select>
                </div>

              </div>

              {/* Product Cards Grid (3 Rows x 4 Columns = 12 Items Max Per Page) */}
              {isProductsLoading ? (
                <ProductSkeletonGrid count={6} darkMode={darkMode} />
              ) : firestoreError ? (
                <div className="text-center py-20 space-y-4 max-w-lg mx-auto">
                  <div className="w-16 h-16 rounded-full bg-rose-950/60 border border-rose-500/40 flex items-center justify-center mx-auto text-rose-400">
                    <AlertCircle className="w-8 h-8" />
                  </div>
                  <h3 className="font-extrabold text-xl text-rose-400">Database Connection Error</h3>
                  <p className="text-xs text-slate-400">
                    Could not load the live product catalog from Firebase Firestore: <strong>{firestoreError}</strong>. Please check your network connection, security rules, or click reload below to retry.
                  </p>
                  <button
                    onClick={() => {
                      setFirestoreError(null);
                      window.location.reload();
                    }}
                    className="px-4 py-2.5 rounded-xl bg-slate-800 hover:bg-slate-700 text-white font-bold text-xs border border-slate-700 cursor-pointer"
                  >
                    Retry Connection
                  </button>
                </div>
              ) : filteredProducts.length === 0 ? (
                <div className="text-center py-20 space-y-4">
                  <div className="w-16 h-16 rounded-full bg-slate-800/60 border border-slate-700 flex items-center justify-center mx-auto text-slate-400">
                    <Filter className="w-8 h-8" />
                  </div>
                  <h3 className="font-extrabold text-xl">No Products Available</h3>
                  <p className="text-xs text-slate-400">There are currently no published products in the catalog or matching your active filters.</p>
                  {(searchQuery || selectedCategory !== 'All' || selectedFormat !== 'All') && (
                    <button
                      onClick={() => {
                        setSearchQuery('');
                        setSelectedCategory('All');
                        setSelectedFormat('All');
                      }}
                      className="px-4 py-2 rounded-xl bg-blue-600 text-white font-bold text-xs"
                    >
                      Reset Filters
                    </button>
                  )}
                </div>
              ) : (
                <>
                  <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-3 gap-6">
                    {paginatedProducts.map((p, idx) => (
                      <ProductCard
                        key={`${p.id}-${idx}`}
                        product={p}
                        currency={currency}
                        isInWishlist={wishlistItems.some(i => i.id === p.id)}
                        onToggleWishlist={handleToggleWishlist}
                        onAddToCart={handleAddToCart}
                        onQuickBuy={handleQuickBuy}
                        onQuickView={handleNavigateProduct}
                        darkMode={darkMode}
                        priority={idx < 6}
                      />
                    ))}
                  </div>

                  {/* Clean Pagination Bar (< Previous 1 2 3 4 5 Next >) */}
                  {totalPages > 1 && (
                    <div className={`mt-12 pt-8 border-t flex flex-col sm:flex-row items-center justify-between gap-4 ${
                      darkMode ? 'border-slate-800/80' : 'border-slate-200'
                    }`}>
                      <div className="text-xs font-bold text-slate-400">
                        Showing <span className="text-cyan-400 font-black">{filteredProducts.length === 0 ? 0 : pStartIndex + 1}</span>–
                        <span className="text-cyan-400 font-black">{pEndIndex}</span> of{' '}
                        <span className="text-cyan-400 font-black">{filteredProducts.length}</span> Products (Page {validPage} of {totalPages})
                      </div>

                      <div className="flex items-center gap-1.5 sm:gap-2 flex-wrap justify-center mt-2 sm:mt-0">
                        {/* Previous Button */}
                        <button
                          onClick={() => {
                            if (validPage > 1) {
                              setCurrentPage(validPage - 1);
                              document.getElementById('featured-products')?.scrollIntoView({ behavior: 'smooth' });
                            }
                          }}
                          disabled={validPage === 1}
                          className={`px-2.5 sm:px-3.5 py-2 rounded-xl text-xs font-black flex items-center gap-1 border transition-all ${
                            validPage === 1
                              ? 'opacity-40 cursor-not-allowed border-slate-800 text-slate-500 bg-slate-900/30'
                              : darkMode
                              ? 'bg-slate-900 border-slate-700 text-slate-200 hover:bg-slate-800 hover:border-cyan-400 hover:text-cyan-300 shadow-md'
                              : 'bg-white border-slate-300 text-slate-700 hover:bg-slate-100 hover:border-blue-500 shadow-sm'
                          }`}
                        >
                          <ChevronLeft className="w-4 h-4 text-cyan-400" /> <span className="hidden sm:inline">Previous</span>
                        </button>

                        {/* Page Number Buttons */}
                        {getPageNumbers(validPage, totalPages).map((page, pIdx) => {
                          if (typeof page === 'string') {
                            return (
                              <span key={`ellipsis-${pIdx}`} className="px-1 sm:px-2 py-1 text-slate-500 text-xs font-black">
                                ...
                              </span>
                            );
                          }
                          const isActive = page === validPage;
                          return (
                            <button
                              key={`page-${page}`}
                              onClick={() => {
                                setCurrentPage(page);
                                document.getElementById('featured-products')?.scrollIntoView({ behavior: 'smooth' });
                              }}
                              className={`w-8 h-8 sm:w-9 sm:h-9 rounded-xl text-xs font-black transition-all border flex items-center justify-center ${
                                isActive
                                  ? 'bg-gradient-to-r from-blue-600 to-cyan-600 text-white border-cyan-300 shadow-lg shadow-cyan-500/25 scale-105'
                                  : darkMode
                                  ? 'bg-slate-900/90 border-slate-800 text-slate-300 hover:bg-slate-800 hover:border-slate-700'
                                  : 'bg-white border-slate-300 text-slate-700 hover:bg-slate-100'
                              }`}
                            >
                              {page}
                            </button>
                          );
                        })}

                        {/* Next Button */}
                        <button
                          onClick={() => {
                            if (validPage < totalPages) {
                              setCurrentPage(validPage + 1);
                              document.getElementById('featured-products')?.scrollIntoView({ behavior: 'smooth' });
                            }
                          }}
                          disabled={validPage === totalPages}
                          className={`px-2.5 sm:px-3.5 py-2 rounded-xl text-xs font-black flex items-center gap-1 border transition-all ${
                            validPage === totalPages
                              ? 'opacity-40 cursor-not-allowed border-slate-800 text-slate-500 bg-slate-900/30'
                              : darkMode
                              ? 'bg-slate-900 border-slate-700 text-slate-200 hover:bg-slate-800 hover:border-cyan-400 hover:text-cyan-300 shadow-md'
                              : 'bg-white border-slate-300 text-slate-700 hover:bg-slate-100 hover:border-blue-500 shadow-sm'
                          }`}
                        >
                          <span className="hidden sm:inline">Next</span> <ChevronRight className="w-4 h-4 text-cyan-400" />
                        </button>
                      </div>
                    </div>
                  )}
                </>
              )}

              {/* Infinite Scrolling Category Marquee Section (Inside container) */}
              <CategoryMarqueeSection
                config={marqueeConfig}
                darkMode={darkMode}
                onSelectCategory={(cat) => {
                  setSelectedCategory(cat as any);
                  const el = document.getElementById('featured-products');
                  if (el) el.scrollIntoView({ behavior: 'smooth' });
                }}
              />

            </div>
          </section>

          {/* Enterprise Testimonials Section */}
          <TestimonialsSection
            testimonials={testimonialsList}
            config={testimonialsConfig}
            darkMode={darkMode}
          />
        </main>
      )}

      {/* Dedicated Marketplace Search Page */}
      {activeTab === 'search' && (
        <SearchResultsPage
          searchQuery={searchQuery}
          setSearchQuery={handlePerformSearch}
          products={allProducts.filter(p => p && isProductPublished(p))}
          isProductsLoading={false}
          hasFirestoreLoaded={isProductsSyncedFromServer}
          currency={currency}
          darkMode={darkMode}
          onQuickView={handleNavigateProduct}
          onAddToCart={handleAddToCart}
                        onQuickBuy={handleQuickBuy}
          onToggleWishlist={handleToggleWishlist}
          cartItems={cartItems}
          wishlistItems={wishlistItems}
          onOpenAI={() => setAiModalOpen(true)}
        />
      )}

      
      {activeTab === 'invoice_view' as any && (
        <CustomerInvoiceView token={invoiceToken} darkMode={darkMode} />
      )}

      {/* Admin / Management View */}
      {activeTab === 'admin' && (
        <React.Suspense fallback={<div className="flex items-center justify-center min-h-screen bg-slate-900"><div className="w-8 h-8 border-4 border-cyan-500 border-t-transparent rounded-full animate-spin"></div></div>}>
          <AdminDashboard
            products={allProducts}
            onAddProduct={handleAddProductAdmin}
            onUpdateProduct={handleUpdateProductAdmin}
            onDeleteProduct={handleDeleteProductAdmin}
            currency={currency}
            darkMode={darkMode}
            cms={cmsContent}
            onUpdateCMS={setCmsContent}
            stats={liveStats}
            orders={userOrders}
            allProducts={allProducts}
            customersList={usersList}
            reviewsList={reviewsList}
            downloadsList={downloadsList}
            dynamicGateways={dynamicGateways}
            onUpdateDynamicGateways={setDynamicGateways}
          />
        </React.Suspense>
      )}

      {/* User Dashboard View */}
      {activeTab === 'user' && (
        <React.Suspense fallback={<div className="flex items-center justify-center min-h-screen bg-slate-900"><div className="w-8 h-8 border-4 border-cyan-500 border-t-transparent rounded-full animate-spin"></div></div>}>
          <UserDashboard
            user={{
              name: 'Valued Customer',
              email: 'customer@rheditography.net',
              avatar: 'https://images.unsplash.com/photo-1534528741775-53994a69daeb?auto=format&fit=crop&w=250&q=80',
              memberSince: 'January 2025',
              purchasesCount: userOrders.length,
              downloadsCount: downloadsList.length,
              twoFactorEnabled: false
            }}
            orders={userOrders}
            allProducts={allProducts}
            wishlistItems={wishlistItems}
            currency={currency}
            onRemoveFromWishlist={handleToggleWishlist}
            onAddToCart={handleAddToCart}
                        onQuickBuy={handleQuickBuy}
            onNavigateProduct={handleNavigateProduct}
            darkMode={darkMode}
          />
        </React.Suspense>
      )}

      {/* Categories View */}
      {activeTab === 'categories' && (
        <CategoriesPage
          darkMode={darkMode}
          selectedCategory={selectedCategory}
          onSelectCategory={(cat) => {
            setSelectedCategory(cat);
            if (cat === 'All') {
              window.history.pushState({ path: '/categories' }, '', '/categories');
              setActiveTab('categories');
            } else {
              handleNavigateCategory(cat);
            }
          }}
          onNavigateHome={() => {
            setSelectedCategory('All');
            handleTabChange('marketplace');
          }}
          products={allProducts.filter(p => p && isProductPublished(p))}
          isProductsLoading={false}
          hasFirestoreLoaded={isProductsSyncedFromServer}
          currency={currency}
          wishlistItems={wishlistItems}
          onToggleWishlist={handleToggleWishlist}
          onAddToCart={handleAddToCart}
                        onQuickBuy={handleQuickBuy}
          onQuickView={handleNavigateProduct}
        />
      )}

      {/* Countries View */}
      {activeTab === 'countries' && (
        <CountriesPage
          darkMode={darkMode}
          selectedCountry={selectedCountry}
          onSelectCountry={handleNavigateCountry}
          products={allProducts.filter(p => p && isProductPublished(p))}
          isProductsLoading={false}
          hasFirestoreLoaded={isProductsSyncedFromServer}
          countriesList={countriesList}
          currency={currency}
          wishlistItems={wishlistItems}
          onToggleWishlist={handleToggleWishlist}
          onAddToCart={handleAddToCart}
                        onQuickBuy={handleQuickBuy}
          onQuickView={handleNavigateProduct}
        />
      )}

      {/* Contact View */}
      {activeTab === 'contact' && (
        <ContactPage
          darkMode={darkMode}
          onOpenAI={() => setAiModalOpen(true)}
          cms={cmsContent}
        />
      )}

      {/* Footer (Hidden in Admin Dashboard) */}
      {activeTab !== 'admin' && (
        <Footer
          darkMode={darkMode}
          cms={cmsContent}
          onOpenAdmin={() => handleTabChange('admin')}
          onSelectCategorySearch={(term) => handlePerformSearch(term)}
          onSelectFormatSearch={(fmt) => {
            setSelectedFormat(fmt as any);
            handleTabChange('marketplace');
          }}
          onOpenLegalModal={(title, tabOrText) => {
            if (tabOrText === 'privacy' || (title ? title.toLowerCase() : '').includes('privacy')) {
              setLegalModalTab('privacy');
            } else if (tabOrText === 'refund' || (title ? title.toLowerCase() : '').includes('refund')) {
              setLegalModalTab('refund');
            } else if (tabOrText === 'faq' || (title ? title.toLowerCase() : '').includes('faq') || (title ? title.toLowerCase() : '').includes('license')) {
              setLegalModalTab('faq');
            } else {
              setLegalModalTab('terms');
            }
          }}
          onOpenSupport={() => handleTabChange('contact')}
        />
      )}

      {/* Interactive Comprehensive Legal Policy Modal */}
      <LegalModal
        isOpen={!!legalModalTab}
        onClose={() => setLegalModalTab(null)}
        defaultTab={legalModalTab || 'terms'}
      />

      {/* Modals & Slide-Overs */}
      <ProductDetailModal
        product={quickViewProduct}
        isLoading={pendingProductSlug !== null}
        onClose={handleCloseProductModal}
        currency={currency}
        isInWishlist={quickViewProduct ? wishlistItems.some(i => i.id === quickViewProduct.id) : false}
        onToggleWishlist={handleToggleWishlist}
        onAddToCart={handleAddToCart}
                        onQuickBuy={handleQuickBuy}
        darkMode={darkMode}
        allProducts={allProducts}
        onSelectProduct={handleNavigateProduct}
        onNavigateCategory={handleNavigateCategory}
      />

      <CartDrawer
        isOpen={cartDrawerOpen}
        onClose={() => setCartDrawerOpen(false)}
        cartItems={cartItems}
        onUpdateQuantity={handleUpdateQuantity}
        onRemoveItem={handleRemoveCartItem}
        appliedCoupon={appliedCoupon}
        onApplyCoupon={handleApplyCoupon}
        onNavigateProduct={handleNavigateProduct}
        onProceedToCheckout={() => {
          const isLoggedIn = localStorage.getItem('rh_user_logged_in') === 'true';
          if (!isLoggedIn) {
            setCartDrawerOpen(false);
            setActiveTab('user');
            showToast('🔒 Sign In Required: Please login to your Customer Account to proceed with Checkout.');
          } else {
            let isVerified = true;
            try {
              const userRaw = localStorage.getItem('rh_current_user');
              if (userRaw) {
                const u = JSON.parse(userRaw);
                if (u && u.emailVerified === false) {
                  isVerified = false;
                }
              }
            } catch (e) {}

            if (!isVerified) {
              setCartDrawerOpen(false);
              setActiveTab('user');
              showToast('🔒 Email Verification Required: Please enter the 6-digit verification code sent to your email.');
            } else {
              setCheckoutModalOpen(true);
            }
          }
        }}
        currency={currency}
        darkMode={darkMode}
      />

      <CheckoutModal
        isOpen={checkoutModalOpen}
        onClose={() => setCheckoutModalOpen(false)}
        cartItems={cartItems}
        appliedCoupon={appliedCoupon}
        currency={currency}
        dynamicGateways={dynamicGateways}
        onNavigateProduct={handleNavigateProduct}
        onOrderComplete={(order) => {
          setUserOrders(prev => [order, ...prev]);
          setCartItems([]);
        }}
        darkMode={darkMode}
      />

      <AIAssistantModal
        isOpen={aiModalOpen}
        onClose={() => setAiModalOpen(false)}
        darkMode={darkMode}
      />


      {/* Toast Notification Floating Banner */}
      {toastMessage && (
        <div className="fixed bottom-6 right-6 z-50 px-5 py-3 rounded-2xl bg-slate-900/90 border border-cyan-400/40 text-cyan-200 text-xs font-bold shadow-2xl flex items-center gap-2 animate-bounce">
          <CheckCircle2 className="w-4 h-4 text-cyan-400" />
          <span>{toastMessage}</span>
        </div>
      )}

    </div>
  );
}
